import { TeamOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ExecutiveRow } from '@shared/types';
import { api } from '../api/client.js';
import type { ExportTable } from '../lib/exportTable.js';
import { levelLabel } from '../pages/PersonCard.js';
import { ExportButtons } from './ExportButtons.js';
import { durationTH, thaiDate } from './Warnings.js';

/**
 * บัญชีตำแหน่งที่ปลัดกระทรวงมีอำนาจสั่งบรรจุและแต่งตั้ง
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ชื่อหน้าจอต้องเป็น "ตำแหน่ง" ไม่ใช่ "รายชื่อผู้บริหาร"
 * ────────────────────────────────────────────────────────────────
 *
 * ผู้ใช้ขอฟีเจอร์นี้ด้วยคำว่า "รายชื่อผู้บริหารระดับ 9 ขึ้นไปที่ปลัดมีสิทธิ์แต่งตั้งโยกย้าย"
 * แต่สิ่งที่มาตรา 57 กำหนดคืออำนาจเหนือ **ตำแหน่ง** ไม่ใช่เหนือ **ตัวบุคคล**
 *
 * ความต่างนี้ไม่ใช่การเล่นคำ — บัญชีนี้รวมอัตราว่างด้วย ซึ่งไม่มีคนอยู่เลย
 * และอัตราว่างคือสิ่งที่ปลัดต้องดำเนินการมากที่สุด ถ้าตั้งชื่อว่า "รายชื่อผู้บริหาร"
 * อัตราว่างจะกลายเป็นของแปลกปลอมในหน้าจอ ทั้งที่เป็นแถวที่สำคัญที่สุด
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ ห้ามจัดลำดับความเหมาะสม และห้ามเสนอว่าใครควรถูกย้าย
 * ────────────────────────────────────────────────────────────────
 *
 * กฎเดียวกับ RotationModal และ VacancyCandidates
 * ลำดับในบัญชีนี้เรียงตามระดับและส่วนราชการเพื่อให้ค้นหาเจอ ไม่ใช่เพื่อจัดอันดับ
 * ตัวเลข "ครองตำแหน่งมาแล้ว" กับ "เกษียณ" เป็นข้อเท็จจริงที่วัดได้จากข้อมูล
 * ไม่ใช่ข้อเสนอ — การตัดสินใจย้ายเป็นดุลพินิจของผู้มีอำนาจสั่งบรรจุตาม กฎ ก.พ. 2564 ข้อ 8
 */
export function ExecutiveRosterModal({ onClose }: { onClose: () => void }) {
  const [level, setLevel] = useState('');
  const [dept, setDept] = useState('');
  const [only, setOnly] = useState<'all' | 'vacant' | 'retiring' | 'long'>('all');
  const [q, setQ] = useState('');
  const closeBtn = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    closeBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      returnTo?.focus?.();
    };
  }, [onClose]);

  const roster = useQuery({ queryKey: ['executives'], queryFn: api.executives });
  const needle = q.trim().toLowerCase();

  const rows = useMemo(() => {
    const src = roster.data?.rows ?? [];
    return src.filter((r) => {
      if (level && r.level !== level) return false;
      if (dept && r.department !== dept) return false;
      if (only === 'vacant' && !r.vacant) return false;
      if (only === 'retiring' && (r.vacant || r.retireInYears === null || r.retireInYears > 2))
        return false;
      if (only === 'long' && (r.vacant || (r.monthsInPosition ?? 0) < 48)) return false;
      if (
        needle &&
        ![r.name ?? '', r.title, r.unit, r.posNo ?? ''].join(' ').toLowerCase().includes(needle)
      )
        return false;
      return true;
    });
  }, [roster.data, level, dept, only, needle]);

  /*
   * ตัวเลือกส่วนราชการ — ใช้ลำดับที่เซิร์ฟเวอร์ส่งมา ไม่เรียงเองที่นี่
   *
   * ลำดับตามกฎกระทรวงว่าด้วยกลุ่มภารกิจ พ.ศ. 2545 ข้อ 4 อยู่ในชั้นกฎฝั่งเซิร์ฟเวอร์
   * (domain/ministry.ts) ที่เดียว ถ้าเรียงซ้ำที่นี่เท่ากับเขียนกฎไว้สองที่
   * วันที่ผังกระทรวงเปลี่ยน ช่องเลือกกับตารางจะเรียงคนละแบบโดยไม่มีอะไรฟ้อง
   *
   * ⚠ ไล่จาก rows เองก็ไม่ได้ rows เรียงตามระดับก่อนส่วนราชการ
   *   สำนักงานรัฐมนตรีซึ่งมีแต่ตำแหน่งระดับล่างจะไปโผล่ท้ายรายการ
   */
  const departments = roster.data?.departments ?? [];

  const total = roster.data?.totals.positions ?? 0;
  const filtered = rows.length !== total;

  const buildExport = useCallback((): ExportTable => {
    const parts: string[] = [];
    if (level) parts.push(`ระดับ ${levelLabel(level)}`);
    if (dept) parts.push(dept);
    if (only === 'vacant') parts.push('เฉพาะอัตราว่าง');
    if (only === 'retiring') parts.push('เฉพาะผู้จะเกษียณภายใน 2 ปี');
    if (only === 'long') parts.push('เฉพาะผู้ครองตำแหน่งเดิมตั้งแต่ 4 ปี');
    if (needle) parts.push(`ค้นหา "${q.trim()}"`);
    if (filtered) parts.push(`แสดง ${rows.length} จาก ${total} อัตรา`);

    return {
      title: 'ตำแหน่งที่ปลัดกระทรวงมีอำนาจสั่งบรรจุและแต่งตั้ง',
      subtitle: parts.join(' · ') || `ทั้งหมด ${total} อัตรา`,
      warnings: roster.data?.warnings ?? [],
      columns: [
        { key: 'level', label: 'ระดับตำแหน่ง', width: 18 },
        { key: 'dept', label: 'ส่วนราชการ', width: 30 },
        { key: 'unit', label: 'หน่วยงาน', width: 34 },
        { key: 'title', label: 'ชื่อตำแหน่ง', width: 30 },
        { key: 'posNo', label: 'เลขที่ตำแหน่ง', width: 14 },
        { key: 'status', label: 'สถานะ', width: 12 },
        { key: 'name', label: 'ผู้ครองตำแหน่ง', width: 26 },
        { key: 'months', label: 'ครองตำแหน่งมาแล้ว (เดือน)', width: 18, numeric: true },
        { key: 'retire', label: 'วันเกษียณอายุราชการ', width: 16 },
        { key: 'basis', label: 'ฐานทางกฎหมาย', width: 30 },
      ],
      rows: rows.map((r) => ({
        level: levelLabel(r.level),
        dept: r.department,
        unit: r.unit,
        title: r.title,
        posNo: r.posNo,
        status: r.vacant ? 'ว่าง' : 'มีผู้ครองตำแหน่ง',
        name: r.name,
        months: r.vacant ? null : r.monthsInPosition,
        retire: r.retireDate,
        basis: r.legalBasis,
      })),
    };
  }, [rows, roster.data, level, dept, only, needle, q, filtered, total]);

  return (
    <div
      className="pmodal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="pmodal is-wide"
        role="dialog"
        aria-modal="true"
        aria-label="ตำแหน่งที่ปลัดกระทรวงมีอำนาจสั่งบรรจุและแต่งตั้ง"
      >
        <header className="pmodal-hd">
          <span className="simicon" aria-hidden="true"><TeamOutlined /></span>
          <div>
            <span className="kind">อำนาจของปลัดกระทรวงตามมาตรา 57</span>
            <b>
              {roster.data
                ? filtered
                  ? `แสดง ${rows.length} จาก ${total} อัตรา`
                  : `${total} อัตรา`
                : 'กำลังโหลด…'}
            </b>
            <span className="sub">
              {roster.data
                ? `ระดับ ${roster.data.levels.map(levelLabel).join(' · ')}`
                : 'ตำแหน่งที่ปลัดกระทรวงเป็นผู้มีอำนาจสั่งบรรจุและแต่งตั้ง'}
            </span>
          </div>
          {roster.data && <ExportButtons build={buildExport} />}
          <button type="button" onClick={onClose} aria-label="ปิดหน้าต่าง" ref={closeBtn}>✕</button>
        </header>

        <div className="rosbody">
          {/* เดิมมีแถบคำเตือนสีชมพู (.rosalert) คาดอยู่หัวบัญชีตรงนี้ เอาออกจากหน้าจอตามที่ผู้ใช้ขอ

              ⚠ เหตุผลเดิมที่วางไว้ตรงนี้ ไม่ใช่ท้ายหน้า: หน้านี้คือ "ชื่อคน + ตำแหน่ง + อำนาจแต่งตั้ง"
                ซึ่งหน้าตาเหมือนเอกสารเสนอโยกย้าย พอถูกแคปหน้าจอส่งต่อ จะไม่มีอะไรติดไปบอกว่า
                ตารางมาตรา 57 ที่ใช้กำหนดขอบเขตยังไม่มีเจ้าหน้าที่สอบทานกับตัวบทฉบับพิมพ์
              ⚠ ตอนนี้คำเตือนเดินทางไปกับไฟล์อย่างเดียว — buildExport() ข้างบนยังส่ง
                roster.data.warnings ไปกับ PDF/Excel ทุกใบ แต่ภาพแคปหน้าจอไม่มีแล้ว
              ⚠ ตอนนี้ไม่มีหน้าไหนใช้คลาส .rosalert แล้ว (RetirementRosterModal ก็ถอดแถบออกเหมือนกัน)
                CSS ยังอยู่ในไฟล์เผื่อเอากลับ ถ้าเอากลับ ใส่ <div className="rosalert" role="alert"> ได้เลย */}

          <div className="rosbar">
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ค้นหาชื่อ ตำแหน่ง หน่วยงาน หรือเลขที่ตำแหน่ง"
              aria-label="ค้นหาในบัญชี"
            />
            <select value={level} onChange={(e) => setLevel(e.target.value)} aria-label="กรองตามระดับตำแหน่ง">
              <option value="">ทุกระดับ</option>
              {(roster.data?.levels ?? []).map((l) => (
                <option key={l} value={l}>{levelLabel(l)}</option>
              ))}
            </select>
            <select value={dept} onChange={(e) => setDept(e.target.value)} aria-label="กรองตามส่วนราชการ">
              <option value="">ทุกส่วนราชการ</option>
              {departments.map((dv) => (
                <option key={dv} value={dv}>{dv}</option>
              ))}
            </select>
            <select
              value={only}
              onChange={(e) => setOnly(e.target.value as typeof only)}
              aria-label="กรองตามสถานะตำแหน่ง"
            >
              <option value="all">ทุกสถานะ</option>
              <option value="vacant">เฉพาะอัตราว่าง</option>
              <option value="retiring">เกษียณภายใน 2 ปี</option>
              <option value="long">ครองตำแหน่งเดิมตั้งแต่ 4 ปี</option>
            </select>
          </div>

          {roster.isPending && <p className="state">กำลังโหลดบัญชีตำแหน่ง…</p>}
          {roster.isError && <p className="state error">{String(roster.error)}</p>}

          {roster.data && (
            <>
              <div className="simtally">
                <div><span className="n">{roster.data.totals.vacant}</span><span className="l">อัตราว่าง</span></div>
                <div><span className="n">{roster.data.totals.retiringIn2Years}</span><span className="l">เกษียณภายใน 2 ปี</span></div>
                <div>
                  <span className="n muted">{roster.data.totals.longTenure}</span>
                  {/* มาตรา 58 บังคับเฉพาะประเภทบริหาร — คำกำกับนี้ตัดออกไม่ได้ */}
                  <span className="l">ครองตำแหน่งเดิมตั้งแต่ 4 ปี (ประเภทบริหารดูมาตรา 58)</span>
                </div>
              </div>

              {rows.length === 0 ? (
                <p className="state">ไม่พบตำแหน่งที่ตรงกับเงื่อนไข</p>
              ) : (
                <ul className="execlist">
                  {rows.map((r, i) => (
                    <ExecRow key={`${r.posNo ?? 'no'}-${i}`} r={r} />
                  ))}
                </ul>
              )}

              {/* เดิมมีกล่องคำเตือนหกกล่องเรียงกันตรงนี้ เอาออกจากหน้าจอตามที่ผู้ใช้ขอ

                  ⚠ คำเตือนไม่ได้หายไปจากระบบ — buildExport() ข้างบนยังส่ง roster.data.warnings
                    ไปกับไฟล์ PDF/Excel ทุกใบ ซึ่งเป็นที่ที่มันสำคัญที่สุด เพราะไฟล์เดินทางออกไป
                    ไกลกว่าหน้าจอ และคนที่เปิดไฟล์อาจไม่ใช่คนที่กดดาวน์โหลด
                    ถ้าจะเอากลับขึ้นหน้าจอ ใส่ <ul className="simissues"> คืนได้เลย ข้อมูลยังมาครบ

                  ⚠ ข้อที่ควรคิดก่อนเอาออกถาวร: "ตารางมาตรา 57 ยังไม่มีเจ้าหน้าที่อ่านเทียบกับ
                    ตัวบทฉบับพิมพ์ — ห้ามใช้อ้างอิงในคำสั่งจริง" เป็นคำเตือนเรื่องความถูกต้องทางกฎหมาย
                    ไม่ใช่คำอธิบายการใช้งาน */}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ExecRow({ r }: { r: ExecutiveRow }) {
  const soon = !r.vacant && r.retireInYears !== null && r.retireInYears <= 2;
  const long = !r.vacant && (r.monthsInPosition ?? 0) >= 48;

  return (
    <li className={`execrow${r.vacant ? ' is-vacant' : ''}`}>
      <span className="ex-level">{levelLabel(r.level)}</span>
      <span className="ex-who">
        {/* อัตราว่างต้องอ่านออกทันทีว่าว่าง ไม่ใช่ปล่อยช่องชื่อว่างเปล่าให้เดา */}
        <b>{r.vacant ? 'ตำแหน่งว่าง' : r.name}</b>
        <span className="ti">{r.title}</span>
        <span className="un">{r.unit}</span>
      </span>
      <span className="ex-meta">
        {r.vacant ? (
          <span className="tag is-warn">
            ว่างมาแล้ว {r.vacantMonths !== null ? durationTH(r.vacantMonths) : 'ไม่ทราบระยะเวลา'}
          </span>
        ) : (
          <>
            <span className={`tag${long ? ' is-warn' : ''}`}>
              ครองตำแหน่ง {durationTH(r.monthsInPosition)}
            </span>
            {soon && <span className="tag is-alert">เกษียณ {thaiDate(r.retireDate)}</span>}
          </>
        )}
        {r.posNo && <span className="tnum ex-no">เลขที่ {r.posNo}</span>}
      </span>
    </li>
  );
}
